/**
 * Dome End Cutout tool.
 *
 * Field workflow:
 *   1. Base ring capture with the prism — 4+ shots around the dome's
 *      tangent to the cylinder. Backend fits a 3-D plane + circle
 *      → dome axis & base diameter.
 *   2. Surface capture in reflectorless mode — spray points across
 *      the dome face. Backend fits a sphere (linear LSQ) → radius &
 *      centre.
 *   3. Add cutouts — tap the numeric fields (or the 3-D view) to
 *      place a circle / rectangle on the fitted sphere.
 *   4. Set-out — TS turns scope to cutout centre. Reflectorless
 *      laser lands on the dome exactly at the cut position.
 *   5. Trace — the TS walks the laser around the outline at an
 *      adjustable lap time so the surveyor can pen-mark it.
 *
 * The 3-D preview uses a light Three.js scene mounted via
 * <SphereScene />. If Three.js fails to load we still render a
 * 2-D fallback so the surveyor can drive the flow from the shot
 * numbers.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { toast } from "sonner";
import { ArrowLeft, Trash, Plus, Target, Circle as CircleIcon, Square as SquareIcon, Radar, StopCircle } from "lucide-react";
import { station, dome as domeApi, fmt } from "../lib/api";
import { usePrismMode } from "../lib/prism-mode";
import { ShootPanel } from "../components/ShootPanel";
import { AimPad } from "../components/AimPad";
import { PrintReportShell, PrintReportButton } from "../components/PrintReportShell";

const STAGE_OPTIONS = ["IN-SITU", "TACK WELD", "POST WELD", "AS BUILT"];

// A single stat card — small, industrial, colour-coded.
const Stat = ({ label, value, hint, color = "#8B939A", testid }) => (
  <div className="rounded-md border border-[#2A2E39] bg-[#0F1116] p-3" data-testid={testid}>
    <div className="text-[9px] uppercase tracking-widest text-[#8B939A]">{label}</div>
    <div className="mt-1 font-mono text-lg font-bold" style={{ color }}>{value}</div>
    {hint && <div className="mt-1 text-[10px] text-[#4A5054]">{hint}</div>}
  </div>
);

/**
 * Tiny 2-D azimuth-elevation cutout picker. Cheaper than dropping
 * Three.js just to place a marker, and works fine for a hemispherical
 * head — we render the sphere as an azimuth (horizontal) x polar
 * (vertical) grid and let the surveyor tap to place / drag to move.
 */
const CutoutPlacer = ({ session, cutouts, selectedId, onPick, onCreate }) => {
  const rows = 12, cols = 24;   // 15° × 15° grid
  return (
    <div className="rounded-md border border-[#2A2E39] bg-[#0F1116] p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">Cutout map (Az × Polar)</span>
        {!session?.surface_ready && (
          <span className="text-[10px] uppercase tracking-widest text-[#F5A623]">Take surface shots first</span>
        )}
      </div>
      <div
        className="relative aspect-[2/1] w-full overflow-hidden rounded-sm border border-[#2A2E39] bg-[#0A0B0E]"
        data-testid="dome-cutout-map"
        onClick={(e) => {
          if (!session?.surface_ready) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const u = (e.clientX - rect.left) / rect.width;    // 0..1 azimuth (0 = axis east)
          const v = (e.clientY - rect.top) / rect.height;    // 0..1 polar (0 = apex)
          const az = u * 2 * Math.PI;
          const polar = v * (Math.PI / 2);   // hemispherical → 0..π/2
          // Convert to unit vector aligned to dome axis.
          const axis = session.ring_axis || [0, 0, 1];
          const n = axis.slice();
          const nLen = Math.hypot(n[0], n[1], n[2]) || 1;
          n[0] /= nLen; n[1] /= nLen; n[2] /= nLen;
          if (n[2] < 0) { n[0] = -n[0]; n[1] = -n[1]; n[2] = -n[2]; }
          // Build tangent basis (u_e east, v_n) with u_e in world XY.
          const east = [Math.abs(n[2]) > 0.99 ? 1 : -n[1], Math.abs(n[2]) > 0.99 ? 0 : n[0], 0];
          const eL = Math.hypot(east[0], east[1], east[2]) || 1;
          east[0] /= eL; east[1] /= eL; east[2] /= eL;
          const north = [
            n[1] * east[2] - n[2] * east[1],
            n[2] * east[0] - n[0] * east[2],
            n[0] * east[1] - n[1] * east[0],
          ];
          // Direction on sphere for (az, polar): n cos p + (east cos az + north sin az) sin p
          const s = Math.sin(polar), c = Math.cos(polar);
          const dx = n[0] * c + (east[0] * Math.cos(az) + north[0] * Math.sin(az)) * s;
          const dy = n[1] * c + (east[1] * Math.cos(az) + north[1] * Math.sin(az)) * s;
          const dz = n[2] * c + (east[2] * Math.cos(az) + north[2] * Math.sin(az)) * s;
          onCreate({ dir_x: dx, dir_y: dy, dir_z: dz });
        }}
        style={{ cursor: session?.surface_ready ? "crosshair" : "not-allowed" }}
      >
        {/* Grid */}
        <svg className="absolute inset-0 h-full w-full" viewBox={`0 0 ${cols} ${rows}`} preserveAspectRatio="none">
          {Array.from({ length: cols + 1 }).map((_, i) => (
            <line key={`c${i}`} x1={i} y1={0} x2={i} y2={rows} stroke="#1A1D24" strokeWidth="0.02" />
          ))}
          {Array.from({ length: rows + 1 }).map((_, i) => (
            <line key={`r${i}`} x1={0} y1={i} x2={cols} y2={i} stroke="#1A1D24" strokeWidth="0.02" />
          ))}
          {/* Apex label */}
          <text x={cols / 2} y={0.35} fill="#4A5054" fontSize="0.35" textAnchor="middle">APEX</text>
          <text x={0.2} y={rows - 0.2} fill="#4A5054" fontSize="0.35">EQ</text>
        </svg>
        {/* Cutout markers */}
        {(cutouts || []).map((c) => {
          const pos = projectCutoutToUV(c, session);
          if (!pos) return null;
          const isSel = c.id === selectedId;
          return (
            <button
              key={c.id}
              data-testid={`dome-cutout-marker-${c.id}`}
              onClick={(e) => { e.stopPropagation(); onPick(c.id); }}
              className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 ${isSel ? "border-[#00E1C7] bg-[#00E1C7]/30" : "border-[#8DC63F] bg-[#8DC63F]/20"}`}
              style={{
                left: `${pos.u * 100}%`,
                top: `${pos.v * 100}%`,
                width: 14, height: 14,
              }}
              title={c.name}
            />
          );
        })}
      </div>
      <div className="mt-2 text-[10px] uppercase tracking-widest text-[#4A5054]">
        Tap map to drop new cutout · left→right = azimuth · top→bottom = pole→equator
      </div>
    </div>
  );
};

// Reverse of the placer's forward projection — take a cutout's world
// direction back to (u, v) grid coords.
function projectCutoutToUV(cutout, session) {
  if (!session?.surface_ready) return null;
  const axis = session.ring_axis || [0, 0, 1];
  const n = axis.slice();
  const nLen = Math.hypot(n[0], n[1], n[2]) || 1;
  n[0] /= nLen; n[1] /= nLen; n[2] /= nLen;
  if (n[2] < 0) { n[0] = -n[0]; n[1] = -n[1]; n[2] = -n[2]; }
  const east = [Math.abs(n[2]) > 0.99 ? 1 : -n[1], Math.abs(n[2]) > 0.99 ? 0 : n[0], 0];
  const eL = Math.hypot(east[0], east[1], east[2]) || 1;
  east[0] /= eL; east[1] /= eL; east[2] /= eL;
  const north = [
    n[1] * east[2] - n[2] * east[1],
    n[2] * east[0] - n[0] * east[2],
    n[0] * east[1] - n[1] * east[0],
  ];
  const d = [cutout.dir_x, cutout.dir_y, cutout.dir_z];
  const dLen = Math.hypot(d[0], d[1], d[2]) || 1;
  d[0] /= dLen; d[1] /= dLen; d[2] /= dLen;
  const cosP = d[0] * n[0] + d[1] * n[1] + d[2] * n[2];
  const polar = Math.acos(Math.max(-1, Math.min(1, cosP)));
  const ex = d[0] - cosP * n[0], ey = d[1] - cosP * n[1], ez = d[2] - cosP * n[2];
  const az = Math.atan2(ex * north[0] + ey * north[1] + ez * north[2],
                        ex * east[0] + ey * east[1] + ez * east[2]);
  const u = ((az / (2 * Math.PI)) + 1) % 1;
  const v = Math.max(0, Math.min(1, polar / (Math.PI / 2)));
  return { u, v };
}

// Convert world (E, N, H) to instrument Hz/V, using the station's
// occupied coord + IH. Same convention as the rest of the app (Hz = 0
// points North, measured clockwise; V = zenith angle).
function worldToHzV(x, y, z, s) {
  const sx = s?.station_x || 0;
  const sy = s?.station_y || 0;
  const sz = (s?.station_z || 0) + (s?.instrument_height || 0);
  const dx = x - sx, dy = y - sy, dz = z - sz;
  const horiz = Math.hypot(dx, dy);
  let hz = Math.atan2(dx, dy);
  if (hz < 0) hz += 2 * Math.PI;
  const v = Math.atan2(horiz, dz);
  return { hz, v };
}

// ---------- Main component --------------------------------------------------

export default function DomeCutout({ onBack }) {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);       // full session
  const [prismMode] = usePrismMode();
  const [tracing, setTracing] = useState(false);
  const traceAbortRef = useRef(false);

  const [selectedCutoutId, setSelectedCutoutId] = useState(null);
  const selectedCutout = useMemo(
    () => current?.cutouts?.find((c) => c.id === selectedCutoutId) || null,
    [current, selectedCutoutId]
  );

  const reloadList = useCallback(async () => {
    try { setSessions(await domeApi.listSessions()); }
    catch { toast.error("Failed to load dome sessions"); }
  }, []);

  const reloadCurrent = useCallback(async () => {
    if (!current?.id) return;
    try { setCurrent(await domeApi.get(current.id)); }
    catch { /* silent */ }
  }, [current?.id]);

  useEffect(() => { reloadList(); }, [reloadList]);

  const createSession = async () => {
    const name = prompt("Dome name (e.g. Vessel V-101 north head)");
    if (!name) return;
    const nominalStr = prompt("Nominal outer diameter in mm (optional)");
    const nominal = nominalStr ? parseFloat(nominalStr) : null;
    try {
      const s = await domeApi.create({ name, nominal_diameter_mm: Number.isFinite(nominal) ? nominal : null });
      await reloadList();
      setCurrent(s);
      setSelectedCutoutId(null);
    } catch { toast.error("Create failed"); }
  };

  const openSession = async (sid) => {
    try { setCurrent(await domeApi.get(sid)); setSelectedCutoutId(null); }
    catch { toast.error("Open failed"); }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this dome session?")) return;
    await domeApi.remove(sid);
    if (current?.id === sid) setCurrent(null);
    await reloadList();
  };

  // ShootPanel calls this with the {hz, v, sd, x, y, z} of every shot.
  const persistShot = async (m, kind) => {
    if (!current?.id) return;
    try {
      const s = await domeApi.addShot(current.id, {
        kind,
        hz: m.hz, v: m.v, sd: m.sd,
        x: m.x ?? m.east ?? 0,
        y: m.y ?? m.north ?? 0,
        z: m.z ?? m.up ?? 0,
      });
      setCurrent(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add shot failed");
    }
  };

  const removeShot = async (shot_id) => {
    if (!current?.id) return;
    setCurrent(await domeApi.removeShot(current.id, shot_id));
  };

  const addCutoutAt = async ({ dir_x, dir_y, dir_z }) => {
    if (!current?.surface_ready) {
      toast.error("Fit the dome surface first (need 4+ reflectorless shots)");
      return;
    }
    const nextName = `Cutout ${(current.cutouts?.length || 0) + 1}`;
    try {
      const s = await domeApi.addCutout(current.id, {
        name: nextName, shape: "circle",
        dir_x, dir_y, dir_z,
        radius_mm: 100, width_mm: 200, height_mm: 150, rotation_deg: 0,
      });
      setCurrent(s);
      const last = s.cutouts[s.cutouts.length - 1];
      setSelectedCutoutId(last?.id || null);
    } catch { toast.error("Add cutout failed"); }
  };

  const patchCutout = async (payload) => {
    if (!current?.id || !selectedCutoutId) return;
    try {
      setCurrent(await domeApi.patchCutout(current.id, selectedCutoutId, payload));
    } catch { toast.error("Cutout update failed"); }
  };

  const deleteCutout = async () => {
    if (!current?.id || !selectedCutoutId) return;
    if (!window.confirm("Delete this cutout?")) return;
    setCurrent(await domeApi.removeCutout(current.id, selectedCutoutId));
    setSelectedCutoutId(null);
  };

  const setoutCutout = async () => {
    if (!current?.id || !selectedCutoutId) return;
    try {
      const geom = await domeApi.setoutGeometry(current.id, selectedCutoutId);
      const st = await station.state();
      const { hz, v } = worldToHzV(geom.world[0], geom.world[1], geom.world[2], st);
      await station.turn({ hz, v });
      toast.success("Turned to cutout centre");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Set-out failed");
    }
  };

  const [lapSec, setLapSec] = useState(10);
  const traceCutout = async () => {
    if (!current?.id || !selectedCutoutId || tracing) return;
    setTracing(true);
    traceAbortRef.current = false;
    try {
      const [{ points }, st] = await Promise.all([
        domeApi.outlineGeometry(current.id, selectedCutoutId, 64),
        station.state(),
      ]);
      if (!points?.length) throw new Error("no outline");
      await station.laser(true);
      const perPoint = Math.max(50, (lapSec * 1000) / points.length);
      for (const p of points) {
        if (traceAbortRef.current) break;
        const { hz, v } = worldToHzV(p[0], p[1], p[2], st);
        try { await station.turn({ hz, v }); } catch { /* keep going */ }
        await new Promise((r) => setTimeout(r, perPoint));
      }
      await station.laser(false);
      toast.success(traceAbortRef.current ? "Trace stopped" : "Trace complete");
    } catch (e) {
      await station.laser(false).catch(() => undefined);
      toast.error(e?.response?.data?.detail || "Trace failed");
    } finally {
      setTracing(false);
      traceAbortRef.current = false;
    }
  };

  const stopTrace = () => { traceAbortRef.current = true; };

  // ---------- Render --------------------------------------------------------

  if (!current) {
    return (
      <main className="mx-auto max-w-6xl p-4 sm:p-6" data-testid="dome-sessions-page">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">Dome End Cutout</div>
            <h2 className="text-3xl font-black text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              SPHERE FIT · CUT LAYOUT
            </h2>
          </div>
          <div className="flex gap-2">
            <button data-testid="dome-new-session" onClick={createSession}
              className="rounded-md border border-[#00E1C7] bg-[#00E1C7]/20 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-[#00E1C7]">
              <Plus size={13} className="mr-1 inline" /> New Dome
            </button>
            <button data-testid="dome-back" onClick={onBack}
              className="rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white">
              <ArrowLeft size={13} className="mr-1 inline" /> Toolbox
            </button>
          </div>
        </div>

        {sessions.length === 0 ? (
          <div className="rounded-md border border-[#2A2E39] bg-[#0F1116] p-8 text-center text-sm text-[#8B939A]">
            No dome sessions yet. Tap <b className="text-white">New Dome</b> to start.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {sessions.map((s) => (
              <div key={s.id} className="rounded-md border border-[#2A2E39] bg-[#0F1116] p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#8B939A]">{s.stage || "no stage"}</div>
                    <div className="text-lg font-black text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>{s.name}</div>
                    <div className="mt-1 text-[11px] text-[#8B939A]">
                      Ring: {s.n_ring} · Surface: {s.n_surface} · Cutouts: {s.n_cutouts}
                      {s.sphere_radius_mm && ` · R=${fmt(s.sphere_radius_mm, 0)}mm`}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`dome-open-${s.id}`} onClick={() => openSession(s.id)}
                      className="rounded-sm border border-[#009E8E] bg-[#009E8E]/20 px-3 py-1 text-[10px] uppercase tracking-widest text-[#009E8E]">Open</button>
                    <button data-testid={`dome-delete-${s.id}`} onClick={() => deleteSession(s.id)}
                      className="rounded-sm border border-[#FF3B30]/60 bg-[#FF3B30]/10 px-2 py-1 text-[10px] text-[#FF3B30] hover:bg-[#FF3B30]/20">
                      <Trash size={11} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    );
  }

  // ---------- Active session view ------------------------------------------

  const ringShots = current.shots.filter((s) => s.kind === "ring");
  const surfaceShots = current.shots.filter((s) => s.kind === "surface");

  return (
    <main className="mx-auto max-w-7xl p-4 sm:p-6" data-testid="dome-active-page">
      <PrintReportShell
        toolName="Dome Cutout"
        sessionName={current.name}
        passFail={(current.ring_ready && current.surface_ready) ? "PASS" : null}
        headline={
          current.ring_ready
            ? `Ø${fmt(2 * current.ring_radius_mm, 0)} mm base · ${current.cutouts?.length || 0} cutouts`
            : "Fit incomplete"
        }
        surveyor="—"
      >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 no-print">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">{current.stage || "Dome"}</div>
          <h2 className="text-2xl font-black text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>{current.name}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(current.ring_ready && current.surface_ready) && <PrintReportButton testid="dome-print-btn" />}
          <select data-testid="dome-stage-select" value={current.stage || ""}
            onChange={async (e) => {
              const stage = e.target.value || null;
              setCurrent(await domeApi.patch(current.id, { stage }));
            }}
            className="rounded-md border border-[#2A2E39] bg-[#14161A] px-2 py-2 text-[10px] uppercase tracking-widest text-white">
            <option value="">— stage —</option>
            {STAGE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button data-testid="dome-sessions" onClick={() => setCurrent(null)}
            className="rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white">
            ← Sessions
          </button>
        </div>
      </div>

      {/* Fit stats row */}
      <div className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-4">
        <Stat testid="dome-ring-diameter" label="Base Diameter" value={current.ring_ready ? `${fmt(2 * current.ring_radius_mm, 0)} mm` : "—"}
          hint={current.ring_ready ? `RMS ${fmt(current.ring_rms_mm, 1)} mm` : "3+ prism shots"}
          color={current.ring_ready ? "#8DC63F" : "#8B939A"} />
        <Stat testid="dome-sphere-radius" label="Sphere R" value={current.surface_ready ? `${fmt(current.sphere_radius_mm, 0)} mm` : "—"}
          hint={current.surface_ready ? `RMS ${fmt(current.sphere_rms_mm, 1)} mm` : "4+ RL shots"}
          color={current.surface_ready ? "#00E1C7" : "#8B939A"} />
        <Stat testid="dome-axis-offset" label="Centre offset" value={current.axis_offset_mm != null ? `${fmt(current.axis_offset_mm, 1)} mm` : "—"}
          hint="sphere centre vs ring plane" />
        <Stat testid="dome-cutout-count" label="Cutouts" value={String(current.cutouts?.length || 0)} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* LEFT — Capture + shots */}
        <div className="space-y-3 lg:col-span-2">
          <CutoutPlacer
            session={current}
            cutouts={current.cutouts}
            selectedId={selectedCutoutId}
            onPick={setSelectedCutoutId}
            onCreate={addCutoutAt}
          />

          {/* Cutout editor */}
          {selectedCutout && (
            <div className="rounded-md border border-[#00E1C7]/60 bg-[#0F1116] p-3" data-testid="dome-cutout-editor">
              <div className="mb-2 flex items-center justify-between">
                <input data-testid="dome-cutout-name" value={selectedCutout.name}
                  onChange={(e) => patchCutout({ name: e.target.value })}
                  className="rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-2 py-1 text-sm font-bold text-white" />
                <button data-testid="dome-cutout-delete" onClick={deleteCutout}
                  className="rounded-sm border border-[#FF3B30]/60 bg-[#FF3B30]/10 px-2 py-1 text-[10px] text-[#FF3B30] hover:bg-[#FF3B30]/20">
                  <Trash size={11} className="inline" /> Delete
                </button>
              </div>
              <div className="mb-2 flex gap-2">
                <button data-testid="dome-cutout-shape-circle" onClick={() => patchCutout({ shape: "circle" })}
                  className={`flex-1 rounded-sm border px-2 py-1 text-[10px] uppercase tracking-widest ${selectedCutout.shape === "circle" ? "border-[#00E1C7] bg-[#00E1C7]/20 text-[#00E1C7]" : "border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A]"}`}>
                  <CircleIcon size={11} className="mr-1 inline" /> Circle
                </button>
                <button data-testid="dome-cutout-shape-rect" onClick={() => patchCutout({ shape: "rectangle" })}
                  className={`flex-1 rounded-sm border px-2 py-1 text-[10px] uppercase tracking-widest ${selectedCutout.shape === "rectangle" ? "border-[#00E1C7] bg-[#00E1C7]/20 text-[#00E1C7]" : "border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A]"}`}>
                  <SquareIcon size={11} className="mr-1 inline" /> Rectangle
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px] uppercase tracking-widest text-[#8B939A]">
                {selectedCutout.shape === "circle" ? (
                  <label className="col-span-2">
                    Radius (mm)
                    <input type="number" data-testid="dome-cutout-radius" value={selectedCutout.radius_mm}
                      onChange={(e) => patchCutout({ radius_mm: parseFloat(e.target.value) || 0 })}
                      className="mt-1 w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-2 py-1 font-mono text-sm text-white" />
                  </label>
                ) : (
                  <>
                    <label>Width (mm)
                      <input type="number" data-testid="dome-cutout-width" value={selectedCutout.width_mm}
                        onChange={(e) => patchCutout({ width_mm: parseFloat(e.target.value) || 0 })}
                        className="mt-1 w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-2 py-1 font-mono text-sm text-white" />
                    </label>
                    <label>Height (mm)
                      <input type="number" data-testid="dome-cutout-height" value={selectedCutout.height_mm}
                        onChange={(e) => patchCutout({ height_mm: parseFloat(e.target.value) || 0 })}
                        className="mt-1 w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-2 py-1 font-mono text-sm text-white" />
                    </label>
                    <label className="col-span-2">Rotation (°)
                      <input type="number" data-testid="dome-cutout-rot" value={selectedCutout.rotation_deg}
                        onChange={(e) => patchCutout({ rotation_deg: parseFloat(e.target.value) || 0 })}
                        className="mt-1 w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-2 py-1 font-mono text-sm text-white" />
                    </label>
                  </>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button data-testid="dome-cutout-setout" onClick={setoutCutout} disabled={tracing}
                  className="rounded-md border border-[#00E1C7] bg-[#00E1C7]/20 py-2 text-xs font-black uppercase tracking-widest text-[#00E1C7] disabled:opacity-40">
                  <Target size={13} className="mr-1 inline" /> Set-out
                </button>
                {tracing ? (
                  <button data-testid="dome-cutout-stoptrace" onClick={stopTrace}
                    className="rounded-md border border-[#FF3B30] bg-[#FF3B30]/15 py-2 text-xs font-black uppercase tracking-widest text-[#FF3B30] animate-pulse">
                    <StopCircle size={13} className="mr-1 inline" /> Stop Trace
                  </button>
                ) : (
                  <button data-testid="dome-cutout-trace" onClick={traceCutout} disabled={tracing || !current.surface_ready}
                    className="rounded-md border border-[#F5A623] bg-[#F5A623]/15 py-2 text-xs font-black uppercase tracking-widest text-[#F5A623] disabled:opacity-40">
                    <Radar size={13} className="mr-1 inline" /> Trace ({lapSec}s)
                  </button>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#8B939A]">
                Lap
                <input type="range" min={3} max={30} step={1} value={lapSec} onChange={(e) => setLapSec(parseInt(e.target.value, 10))}
                  data-testid="dome-lap-slider" className="flex-1 accent-[#F5A623]" />
                <span className="w-8 text-right font-mono text-white">{lapSec}s</span>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — Capture rail */}
        <div className="space-y-3">
          <div className="rounded-md border border-[#2A2E39] bg-[#0F1116] p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#8B939A]">
              {prismMode ? "Prism · shoot base ring" : "Reflectorless · shoot dome surface"}
            </div>
            <div className="mt-1 mb-2 text-[11px] text-white">
              {prismMode
                ? `Base ring: ${ringShots.length} shots (need 3+)`
                : `Surface: ${surfaceShots.length} shots (need 4+)`}
            </div>
            <ShootPanel
              data-testid="dome-shoot-panel"
              onShot={(m) => persistShot(m, prismMode ? "ring" : "surface")}
              label={prismMode ? `Ring shot #${ringShots.length + 1}` : `Surface shot #${surfaceShots.length + 1}`}
            />
          </div>
          <AimPad tool="dome" sessionId={current.id} />

          {/* Shot lists */}
          <div className="rounded-md border border-[#2A2E39] bg-[#0F1116] p-3">
            <div className="mb-1 text-[10px] uppercase tracking-widest text-[#8B939A]">Base ring shots ({ringShots.length})</div>
            <div className="max-h-32 overflow-y-auto">
              {ringShots.map((sh, i) => (
                <div key={sh.id} className="flex items-center justify-between border-b border-[#1A1D24] py-1 text-[11px]">
                  <span className="font-mono text-[#8DC63F]">R{i + 1}</span>
                  <span className="font-mono text-[#8B939A]">sd {fmt(sh.sd, 3)}m</span>
                  <button data-testid={`dome-remove-ring-${sh.id}`} onClick={() => removeShot(sh.id)}
                    className="text-[#FF3B30] hover:text-[#FF6B60]"><Trash size={11} /></button>
                </div>
              ))}
              {ringShots.length === 0 && <div className="py-2 text-center text-[10px] text-[#4A5054]">no ring shots yet</div>}
            </div>
          </div>
          <div className="rounded-md border border-[#2A2E39] bg-[#0F1116] p-3">
            <div className="mb-1 text-[10px] uppercase tracking-widest text-[#8B939A]">Surface shots ({surfaceShots.length})</div>
            <div className="max-h-40 overflow-y-auto">
              {surfaceShots.map((sh, i) => (
                <div key={sh.id} className="flex items-center justify-between border-b border-[#1A1D24] py-1 text-[11px]">
                  <span className="font-mono text-[#00E1C7]">S{i + 1}</span>
                  <span className="font-mono text-[#8B939A]">
                    {sh.surface_residual_mm != null ? `Δ ${fmt(sh.surface_residual_mm, 1)}mm` : `sd ${fmt(sh.sd, 3)}m`}
                  </span>
                  <button data-testid={`dome-remove-surface-${sh.id}`} onClick={() => removeShot(sh.id)}
                    className="text-[#FF3B30] hover:text-[#FF6B60]"><Trash size={11} /></button>
                </div>
              ))}
              {surfaceShots.length === 0 && <div className="py-2 text-center text-[10px] text-[#4A5054]">no surface shots yet</div>}
            </div>
          </div>
        </div>
      </div>
      </PrintReportShell>
    </main>
  );
}
